/**
 * New-order form modal extracted from Orders.jsx.
 *
 * Stateless: receives form data + handlers from the host page. The host
 * still owns the dialog open/close state so it can drive other side
 * effects (resetForm on close, e.g.).
 *
 * Mounted in Orders.jsx as:
 *   <OrderFormDialog
 *     open={...} onOpenChange={...}
 *     formData={...} setFormData={...}
 *     selectedCustomer={...} pickCustomer={...} clearCustomer={...}
 *     customers={...} sellers={...} products={...}
 *     handlers={{ quickAddFromCatalog, setLineQty, setLinePrice, removeLineAt }}
 *     onSubmit={...}
 *   />
 */
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { Autocomplete } from "@/components/Autocomplete";
import { POSProductGrid, POSCart } from "@/components/pos/POSBuilder";
import { OrderCustomerCard } from "./OrderCustomerCard";

export default function OrderFormDialog({
  open,
  onOpenChange,
  formData,
  setFormData,
  selectedCustomer,
  pickCustomer,
  clearCustomer,
  customers,
  sellers,
  products,
  quickAddFromCatalog,
  setLineQty,
  setLinePrice,
  removeLineAt,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="btn-primary rounded-sm" data-testid="add-order-button">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Pedido
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuevo Pedido</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Cliente *</label>
              {selectedCustomer ? (
                // Customer selected — render an editable-only-via-Clientes
                // summary card. All identification data is locked here.
                <OrderCustomerCard customer={selectedCustomer} onClear={clearCustomer} />
              ) : (
                <Autocomplete
                  items={customers}
                  value={formData.nombre_cliente}
                  onChange={(v) => setFormData({ ...formData, nombre_cliente: v, cliente_id: "" })}
                  onSelect={pickCustomer}
                  searchKeys={["nombre_facturacion", "nombre_fantasia", "phone"]}
                  placeholder="Empezá a escribir para elegir un cliente del catálogo…"
                  testid="order-customer-input"
                  autoFocus
                  inputClassName="rounded-sm"
                  renderItem={(c) => {
                    const incomplete = !(c.phone || "").trim();
                    return (
                      <div className="flex justify-between items-center gap-2">
                        <span className="font-medium truncate">{c.nombre_facturacion}</span>
                        <div className="flex items-center gap-1.5">
                          {(c.nombre_fantasia || c.phone) && (
                            <span className="text-xs text-slate-500 truncate">
                              {c.nombre_fantasia || c.phone}
                            </span>
                          )}
                          {incomplete && (
                            <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200">
                              Sin teléfono
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
              )}
              {!selectedCustomer && (
                <p className="mt-1 text-[11px] text-slate-500 italic">
                  Solo se aceptan clientes del catálogo. ¿No existe? Creá el cliente primero desde Clientes.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Vendedor *</label>
              <Autocomplete
                testid="order-seller-input"
                value={formData.nombre_vendedor}
                onChange={(value) => setFormData({ ...formData, nombre_vendedor: value })}
                onSelect={(s) => setFormData({ ...formData, nombre_vendedor: s.nombre_completo })}
                items={sellers}
                searchKeys={["nombre_completo", "codigo_vendedor"]}
                placeholder={sellers.length > 0 ? "Buscá un vendedor…" : "Nombre del vendedor"}
                renderItem={(s) => (
                  <div className="flex justify-between items-center gap-2">
                    <span className="font-medium truncate">{s.nombre_completo}</span>
                    {s.codigo_vendedor && (
                      <span className="text-[10px] mono text-slate-500 truncate">{s.codigo_vendedor}</span>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          <div className="border border-slate-200 rounded-sm p-4 space-y-4">
            <POSProductGrid
              products={products}
              lineas={formData.lineas}
              onAddProduct={quickAddFromCatalog}
              testid="order-pos-grid"
              emptyHint="No hay productos en el catálogo. Creá uno desde Productos."
            />

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Carrito del pedido</label>
                <span className="text-[11px] uppercase tracking-wide text-slate-500">
                  Tocá +1 / +5 / +10 para cantidades; editá precio para descuentos
                </span>
              </div>
              <POSCart
                lineas={formData.lineas}
                onChangeQty={setLineQty}
                onChangePrice={setLinePrice}
                onRemove={removeLineAt}
                testid="order-pos-cart"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="btn-primary rounded-sm"
              disabled={!selectedCustomer || !(selectedCustomer.phone || "").trim() || formData.lineas.length === 0}
              data-testid="save-order-button"
              title={
                !selectedCustomer ? "Elegí un cliente del catálogo" :
                !(selectedCustomer.phone || "").trim() ? "El cliente necesita un teléfono" :
                formData.lineas.length === 0 ? "Agregá al menos una línea" : "Crear pedido"
              }
            >
              Registrar Pedido
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
